import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { manuallyUpdateView } from '@adornis/materialized-views/server/view-manager.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { getNextAccountingReference } from '@martinus/accounting/server/AccountingReferences.js';
import { Group } from '../../groups/entities/Group.js';
import { abacOperationMiddleware } from '../../permissions/middlewares/abacOperationMiddleware.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import {
  checkPermissionMiddleware,
  checkPermissionMiddlewareFunction,
} from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { Person } from '../../users/entities/person.js';
import { BankDaten } from '../../users/profile/entities/BankDaten.js';
import { ProfilDaten } from '../../users/profile/entities/ProfilDaten.js';
import { SepaExportSettings } from '../entities/SepaExportSettings.js';
import { SepaMandat } from '../entities/SepaMandat.js';
import { SepaMandateActions } from '../SepaMandatePolicy.js';
import { isNegativeRuntime } from './mandateActivity.js';
import { isAcceptance } from './MandatStatus.js';
import { assertNoOverlappingSiblingMandate } from '../server/assertNoOverlappingSiblingMandate.js';
import { resolveExportSepa } from '../server/export/resolveExportSepa.js';
import { generateSepaMandatTemplates } from '../server/generateSepaMandatTemplates.js';
import { resolveAcceptAllSepaMandatesForGroup } from '../server/resolvers/resolveAcceptAllSepaMandatesForGroup.js';
import { resolveNotifyForDeletedIBAN } from '../server/resolvers/resolveNotifyForDeletedIBAN.js';
import { resolveReframeAllSepaMandatesForGroup } from '../server/resolvers/resolveReframeAllSepaMandatesForGroup.js';
import { resolveReframeSepaMandat } from '../server/resolvers/resolveReframeSepaMandat.js';
import { resolveSubscribeSepaActivatedGroupsByBPAndSchool } from '../server/resolvers/resolveSubscribeSepaActivatedGroupsByBPAndSchool.js';
import { resolveSubscribeSepaMandatesForBookings } from '../server/resolvers/resolveSubscribeSepaMandatesForBookings.js';
import {
  resolveSubscribeFaultySepaMandatesForUser,
  resolveSubscribeSepaMandatesForUser,
} from '../server/resolvers/resolveSubscribeSepaMandatesForUser.js';

const subscribeSepaMandatsForUser = registerSubscription({
  type: () => [SepaMandat],
  operationName: 'subscribeSepaMandatsForUser',
  params: [
    { type: () => ID, name: 'userID' },
    { type: () => String, name: 'name' },
  ],
  middlewares: [
    checkPermissionMiddlewareFunction((user, args) => {
      // if the user has the permission to view others we will allow a different userID
      if (user.hasPermission(Permission.VIEW_OTHER_SEPA_MANDATS, ANY_CONTEXT)) {
        return true;
      }

      return user._id === args.userID;
    }),
  ],
  resolve: resolveSubscribeSepaMandatesForUser,
});

const subscribeFaultySepaMandatsForUser = registerSubscription({
  type: () => [SepaMandat],
  operationName: 'subscribeFaultySepaMandatsForUser',
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [
    checkPermissionMiddlewareFunction((user, args) => {
      // if the user has the permission to view others we will allow a different userID
      if (user.hasPermission(Permission.VIEW_OTHER_SEPA_MANDATS, ANY_CONTEXT)) {
        return true;
      }

      return user._id === args.userID;
    }),
  ],
  resolve: resolveSubscribeFaultySepaMandatesForUser,
});

const getSepaMandateTemplatesForUser = registerQuery({
  type: () => [SepaMandat],
  operationName: 'getSepaMandateTemplatesForUser',
  params: [
    { type: () => ID, name: 'userID' },
    { type: () => String, name: 'name' },
  ],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  middlewares: [
    checkPermissionMiddlewareFunction((user, args) => {
      // if the user has the permission to view others we will allow a different userID
      if (user.hasPermission(Permission.VIEW_OTHER_SEPA_MANDATS, ANY_CONTEXT)) {
        return true;
      }

      return user._id === args.userID;
    }),
  ],
  resolve:
    (userID: string, name?: string) =>
    <Sel extends BaseQLSelectionSet<SepaMandat>>(gqlFields: Sel) =>
      generateSepaMandatTemplates(userID, name) as unknown as Promise<NarrowedEntity<SepaMandat, Sel>[]>,
});

/**
 * The stored row a save writes to: by `_id`, or by Mandatsreferenz for a template that takes an
 * ended mandate over (`generateSepaMandatTemplates`). A new mandate has neither.
 */
const findStoredMandate = async <P extends Record<string, 1>>(mandat: SepaMandat, projection: P) => {
  const selector = mandat._id
    ? { _id: mandat._id }
    : mandat.mandateReference
      ? { mandateReference: mandat.mandateReference }
      : null;
  if (!selector) return null;

  const mandateCol = await getRawCollection<EntityData<SepaMandat>>(SepaMandat._collectionName);
  return mandateCol.findOne(selector, { projection });
};

const saveMandat = registerMutation({
  type: () => ID,
  operationName: 'saveMandat',
  params: [{ type: () => SepaMandat, name: 'mandat' }],
  middlewares: [
    checkPermissionMiddlewareFunction(async (user, args) => {
      // if the user has the permission to view others we will allow a different userID
      if (user.hasPermission(Permission.VIEW_OTHER_SEPA_MANDATS, ANY_CONTEXT)) {
        return true;
      }

      /**
       * Ownership is judged on the stored mandate - the `acceptedByID` sent along is whatever the
       * client claims. An unaccepted one belongs to nobody yet; an ended one is taken over again only
       * by the person who accepted it (`findReusableMandate`).
       */
      const stored = await findStoredMandate(args.mandat, { accepted: 1, acceptedByID: 1 });
      if (stored?.accepted && stored.acceptedByID !== user._id) return false;

      return user._id === args.mandat.acceptedByID;
    }),
  ],
  resolve: (mandat: SepaMandat) => async () => {
    /**
     * The card refuses a negative runtime on the field already, but this is the boundary. Measured
     * against the stored runtime, so a mandate that already carries one stays editable.
     */
    const stored = await findStoredMandate(mandat, { start: 1, end: 1, accepted: 1, rhythm: 1, billedOn: 1 });
    // a template taking an ended row over arrives with the row's Mandatsreferenz only
    if (stored) mandat._id = stored._id;
    if (isNegativeRuntime(mandat, stored))
      throw new ExpectedError('Ein SEPA-Mandat kann nicht vor seinem Start enden.');

    /**
     * Every acceptance - the card and `resolveAcceptAllSepaMandatesForGroup`, a new mandate or an
     * ended row taken over - comes through here, so one guard keeps a booking from ever billing the
     * same position twice.
     */
    if (isAcceptance(stored, mandat)) await assertNoOverlappingSiblingMandate(mandat);

    if (mandat._id) {
      await mandat.save();
    } else {
      /**
       * Update the mandate reference counter
       */
      mandat.mandateReference = await getNextAccountingReference();

      await mandat.create();
    }

    // if the user has given an iban and not already one in its data we can write it as default
    if (mandat.acceptedByID) {
      const col = await getRawCollection<EntityData<Person>>(Person._collectionName);
      const user = await col.findOne({ _id: mandat.acceptedByID });
      if (user && mandat.iban && !user.profilDaten?.bankInfos?.iban) {
        const userConstructed: Person = constructValue(user);
        userConstructed.profilDaten ??= new ProfilDaten({});
        userConstructed.profilDaten.bankInfos ??= new BankDaten({});
        userConstructed.profilDaten.bankInfos.iban = mandat.iban;
        // a write of the profile only, not `save()`: saving validates every field of the person, so an
        // unrelated stored value (a username with a leading space, in prod) failed the mandate after it was
        // already saved. The whole profile is set because `profilDaten`/`bankInfos` can be null in storage,
        // and a dotted path into a null parent is rejected by Mongo.
        // @ts-expect-error toJSON() is the stored shape, the document type names the entity class
        await col.updateOne({ _id: user._id }, { $set: { profilDaten: userConstructed.profilDaten.toJSON() } });
      }
    }
    /**
     * Also update all childrens as they save their parents sepa mandates aswell
     */
    const userCol = await getRawCollection<EntityData<Person>>(Person._collectionName);
    const user = await userCol.findOne(
      { _id: mandat.acceptedByID },
      { projection: { _id: 1, sorgeberechtigteKinderIDs: 1 } },
    );
    if (user) {
      await manuallyUpdateView(Person, [user._id, ...(user.sorgeberechtigteKinderIDs ?? [])]);
    }
  },
});

const exportMandats = registerSubscription({
  operationName: 'exportMandats',
  middlewares: [checkPermissionMiddleware(Permission.ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION, ANY_CONTEXT)],
  type: () => String,
  params: [{ type: () => SepaExportSettings, name: 'settings' }],
  resolve: resolveExportSepa,
});

const acceptAllSepaMandatesForGroup = registerMutation({
  operationName: 'acceptAllSepaMandatesForGroup',
  type: () => [ID],
  params: [
    { type: () => ID, name: 'groupID' },
    { type: () => [ID], name: 'bookingIdWhitelist' },
  ],
  middlewares: [checkPermissionMiddleware(Permission.ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION, ANY_CONTEXT)],
  resolve: resolveAcceptAllSepaMandatesForGroup,
});

const subscribeSepaMandatesForBookings = registerSubscription({
  operationName: 'subscribeSepaMandatesForBookings',
  type: () => [SepaMandat],
  params: [{ type: () => [ID], name: 'bookingIDs' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeSepaMandatesForBookings,
});

const subscribeSepaActivatedGroupsByBPAndSchool = registerSubscription({
  operationName: 'subscribeSepaActivatedGroupsByBPAndSchool',
  type: () => [Group],
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
  middlewares: [authenticatedMiddleware],
  resolve: resolveSubscribeSepaActivatedGroupsByBPAndSchool,
});

const notifyForDeletedIBAN = registerMutation({
  type: () => Boolean,
  operationName: 'notifyForDeletedIBAN',
  middlewares: [authenticatedMiddleware],
  params: [
    { type: () => ID, name: 'userID' },
    { type: () => String, name: 'deletedIBAN' },
    { type: () => Boolean, name: 'removeActive' },
  ],
  resolve: resolveNotifyForDeletedIBAN,
});

const reframeSepaMandat = registerMutation({
  type: () => ID,
  operationName: 'reframeSepaMandat',
  params: [{ type: () => ID, name: 'mandatID' }],
  middlewares: [abacOperationMiddleware(SepaMandateActions.SEPA_MANDATE_REFRAME)],
  resolve: resolveReframeSepaMandat,
});

const reframeAllSepaMandatesForGroup = registerMutation({
  type: () => [ID],
  operationName: 'reframeAllSepaMandatesForGroup',
  params: [{ type: () => ID, name: 'groupID' }],
  middlewares: [checkPermissionMiddleware(Permission.ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION, ANY_CONTEXT)],
  resolve: resolveReframeAllSepaMandatesForGroup,
});

export const SepaAPI = {
  subscribeMandats: subscribeSepaMandatsForUser,
  subscribeFaultyMandats: subscribeFaultySepaMandatsForUser,
  getTemplates: getSepaMandateTemplatesForUser,
  save: saveMandat,
  export: exportMandats,
  acceptAllMandatesForGroup: acceptAllSepaMandatesForGroup,
  subscribeForBookings: subscribeSepaMandatesForBookings,
  subscribeActivatedGroupsByBPAndSchool: subscribeSepaActivatedGroupsByBPAndSchool,
  checkSepa: notifyForDeletedIBAN,
  reframe: reframeSepaMandat,
  reframeAllForGroup: reframeAllSepaMandatesForGroup,
};
